import { FunctionComponent, useEffect, useState } from "react";
import { Send } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { INITIAL_ENVIO_FORM, EnvioForm } from "../../../models/Envio";
import { createEnvio, setModalAddStatus } from "../../../store/EnviosSlice";
import { WalletService } from "../../../services/WalletService";
import { Modal, FormField } from "../../ui";

const ModalAddEnvio: FunctionComponent = () => {
  const dispatch    = useAppDispatch();
  const modalStatus = useAppSelector((s) => s.envios.modalAddStatus);
  const creating    = useAppSelector((s) => s.envios.creating);
  const clientList  = useAppSelector((s) => s.user.clientList);
  const assetsList  = useAppSelector((s) => s.assets.assets);
  const networks    = useAppSelector((s) => s.assets.networks);

  const [form, setForm] = useState<EnvioForm>({ ...INITIAL_ENVIO_FORM, date: new Date() });

  useEffect(() => {
    if (!modalStatus) setForm({ ...INITIAL_ENVIO_FORM, date: new Date() });
  }, [modalStatus]);

  const change = (field: keyof EnvioForm, value: any) =>
    setForm((p) => ({ ...p, [field]: value }));

  // Carteiras já cadastradas do cliente selecionado (autocomplete da hash).
  const [clientWallets, setClientWallets] = useState<any[]>([]);
  const [showWalletSuggestions, setShowWalletSuggestions] = useState(false);

  useEffect(() => {
    const cid = Number(form.user_id);
    if (!cid) { setClientWallets([]); return; }
    let cancelled = false;
    WalletService.getHwByUserId(cid)
      .then((res: any) => { if (!cancelled) setClientWallets(Array.isArray(res) ? res : []); })
      .catch(() => { if (!cancelled) setClientWallets([]); });
    return () => { cancelled = true; };
  }, [form.user_id]);

  // só carteiras crypto (com hash), filtradas pelo que foi digitado.
  const walletSuggestions = clientWallets
    .filter((w: any) => w.hash)
    .filter((w: any) => !form.hash || String(w.hash).toLowerCase().includes(form.hash.toLowerCase()));

  // ao escolher uma carteira, preenche a hash E a rede.
  const pickWallet = (w: any) => {
    setForm((p) => ({ ...p, hash: w.hash, network_id: w.network?.id ?? p.network_id }));
    setShowWalletSuggestions(false);
  };

  const isValid = () =>
    !!form.user_id && !!form.asset_id && !!form.hash && !!form.network_id &&
    Number(form.value) > 0 && !!form.date;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid() || creating) return;
    dispatch(createEnvio(form));
  };

  const close = () => { if (!creating) dispatch(setModalAddStatus(false)); };
  const selectedAsset = assetsList?.find((a: any) => a.id === Number(form.asset_id));

  return (
    <Modal
      open={modalStatus}
      onClose={close}
      icon={Send}
      title="Adicionar envio"
      subtitle="envio da empresa para o cliente (debita empresa e cliente)"
      maxWidth={640}
      footer={
        <>
          <button type="button" className="btn-pill ghost" onClick={close} disabled={creating}>Cancelar</button>
          <button type="submit" form="modal-add-envio-form" className="btn-pill primary" disabled={!isValid() || creating}>
            {creating ? "Salvando..." : "Adicionar envio"}
          </button>
        </>
      }
    >
      <form id="modal-add-envio-form" onSubmit={submit}>

        <div className="form-row">
          <FormField label="Cliente" htmlFor="e-client">
            <select id="e-client" value={form.user_id} onChange={(e) => change("user_id", e.target.value)}>
              <option value="">Selecione o cliente</option>
              {clientList?.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </FormField>
          <FormField label="Asset" htmlFor="e-asset">
            <select id="e-asset" value={form.asset_id} onChange={(e) => change("asset_id", e.target.value)}>
              <option value="">Selecione o asset</option>
              {assetsList?.map((a: any) => <option key={a.id} value={a.id}>{a.name} ({a.symbol})</option>)}
            </select>
          </FormField>
        </div>

        <div className="form-row">
          <FormField label="Carteira de destino" htmlFor="e-hash">
            <div className="depositor-autocomplete">
              <input
                id="e-hash"
                autoComplete="off"
                value={form.hash}
                onChange={(e) => change("hash", e.target.value)}
                onFocus={() => setShowWalletSuggestions(true)}
                onBlur={() => setTimeout(() => setShowWalletSuggestions(false), 150)}
                placeholder="hash / endereço da carteira"
              />
              {showWalletSuggestions && walletSuggestions.length > 0 && (
                <ul>
                  {walletSuggestions.map((w: any) => (
                    <li key={w.id} onMouseDown={() => pickWallet(w)}>
                      {w.hash} — <b>{w.network?.name ?? "rede —"}</b>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </FormField>
          <FormField label="Rede" htmlFor="e-network">
            <select id="e-network" value={form.network_id} onChange={(e) => change("network_id", e.target.value)}>
              <option value="">Selecione a rede</option>
              {networks?.map((n: any) => <option key={n.id} value={n.id}>{n.name}</option>)}
            </select>
          </FormField>
        </div>

        <div className="form-row">
          <FormField label={`Valor${selectedAsset ? ` (${selectedAsset.symbol})` : ""}`} htmlFor="e-value">
            <input id="e-value" type="number" step="0.00000001" placeholder="0,00" value={form.value} onChange={(e) => change("value", e.target.value)} />
          </FormField>
          <FormField label="Data" htmlFor="e-date">
            <DatePicker id="e-date" selected={form.date} onChange={(d: any) => change("date", d)} dateFormat="dd/MM/yyyy" placeholderText="Selecione a data" maxDate={new Date()} />
          </FormField>
        </div>

        <div className="form-row">
          <FormField label="Status" htmlFor="e-status">
            <input id="e-status" value="Pendente" disabled readOnly />
          </FormField>
          <div style={{ flex: 1 }} />
        </div>

        <p className="info-text">
          O envio debita o valor da carteira do asset da empresa <strong>e</strong> da carteira do cliente.
          Status inicial: <strong>pendente</strong>.
        </p>
      </form>
    </Modal>
  );
};

export default ModalAddEnvio;
